import { useAuthActions } from "@convex-dev/auth/react";
import { useState, useRef } from "react";
import { useConvexAuth } from "convex/react";
import { Redirect } from "wouter";
import { useMountEffect } from "../hooks/use-mount-effect";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Mail, Lock, User, ShieldCheck } from "lucide-react";
import {
  handleAuthError,
  interpretSignInClientReturn,
} from "../lib/auth-errors";

const SIGN_IN_ERROR_ID = "signin-form-error-summary";

/** max lengths align with common identity limits without trusting the server */
const EMAIL_MAX_LEN = 254;
const PASSWORD_MAX_LEN = 256;

export function SignIn() {
  const { signIn } = useAuthActions();
  const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
  const [step, setStep] = useState<"signUp" | "signIn">("signIn");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useMountEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  });

  if (isAuthenticated && !authLoading) {
    return <Redirect to="/dashboard" />;
  }

  const clearErrorAndFocusEmail = () => {
    setError(null);
    const el = emailInputRef.current;
    if (el) {
      el.focus();
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    const form = event.currentTarget;
    const formData = new FormData(form);

    const emailRaw = formData.get("email");
    const passwordRaw = formData.get("password");
    if (typeof emailRaw !== "string" || typeof passwordRaw !== "string") {
      const msg =
        "This form could not be read correctly. Refresh the page and try again.";
      setError(msg);
      setIsLoading(false);
      toast.error(msg);
      return;
    }

    const emailTrimmed = emailRaw.trim();
    if (emailTrimmed.length === 0) {
      const msg = "Enter your work email, then try again.";
      setError(msg);
      setIsLoading(false);
      toast.error(msg);
      return;
    }
    if (emailTrimmed.length > EMAIL_MAX_LEN) {
      const msg =
        "That email is too long. Use a shorter address or ask your admin for help.";
      setError(msg);
      setIsLoading(false);
      toast.error(msg);
      return;
    }
    if (passwordRaw.length > PASSWORD_MAX_LEN) {
      const msg =
        "That password is too long. If you use a password manager, shorten the saved password or contact your admin.";
      setError(msg);
      setIsLoading(false);
      toast.error(msg);
      return;
    }

    formData.set("email", emailTrimmed);

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error("AuthenticationTimeout"));
      }, 15000);
    });

    const signInPromise = signIn("password", formData);

    type SignInResult =
      | { success: true; value: unknown }
      | { success: false; error: { message: string; code: string | null } };

    const result: SignInResult = await Promise.race([
      signInPromise,
      timeoutPromise,
    ])
      .then((value) => {
        return { success: true as const, value };
      })
      .catch((err) => {
        const errorResult = handleAuthError(err);
        return { success: false as const, error: errorResult };
      });

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    if (!mountedRef.current) {
      return;
    }

    if (result.success === false) {
      const errorMessage = result.error.message;
      setError(errorMessage);
      setIsLoading(false);
      toast.error(errorMessage);
      return;
    }

    const flow = interpretSignInClientReturn(result.value);
    if (flow.ok === false) {
      const errorMessage = flow.error.message;
      setError(errorMessage);
      setIsLoading(false);
      toast.error(errorMessage);
      return;
    }
  };

  const formBusy = isLoading || authLoading;

  return (
    <div className="min-w-0 space-y-7 [overflow-wrap:anywhere]">
      <div className="min-w-0 space-y-2 text-center">
        <h2 className="text-balance break-words text-[1.65rem] font-semibold leading-tight tracking-tight text-zinc-950 sm:text-[1.75rem] [-webkit-font-smoothing:antialiased] [text-rendering:optimizeLegibility]">
          {step === "signIn" ? "Welcome back" : "Create an account"}
        </h2>
        <p className="mx-auto max-w-full px-1 text-sm font-normal leading-relaxed text-zinc-600 sm:max-w-md [-webkit-font-smoothing:antialiased] [text-rendering:optimizeLegibility] [overflow-wrap:anywhere] hyphens-auto">
          {step === "signIn"
            ? "Sign in with your work email to open assignments, residents, and internal tools."
            : "Get started with your new account today"}
        </p>
      </div>

      <form
        lang="en"
        aria-label="Staff sign-in"
        aria-busy={formBusy}
        onSubmit={handleSubmit}
        className="min-w-0 space-y-4"
      >
        {step === "signUp" && (
          <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label
                htmlFor="firstName"
                className="text-xs font-semibold text-zinc-700 [-webkit-font-smoothing:antialiased]"
              >
                First name
              </Label>
              <div className="relative min-w-0">
                <User
                  aria-hidden
                  className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="John"
                  maxLength={100}
                  spellCheck={false}
                  autoComplete="given-name"
                  className="h-11 min-w-0 border-zinc-200/90 bg-white/80 ps-10 text-base shadow-sm transition-[box-shadow,border-color] focus-visible:border-zinc-400 md:text-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="lastName"
                className="text-xs font-semibold text-zinc-700 [-webkit-font-smoothing:antialiased]"
              >
                Last name
              </Label>
              <div className="relative min-w-0">
                <User
                  aria-hidden
                  className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Doe"
                  maxLength={100}
                  spellCheck={false}
                  autoComplete="family-name"
                  className="h-11 min-w-0 border-zinc-200/90 bg-white/80 ps-10 text-base shadow-sm transition-[box-shadow,border-color] focus-visible:border-zinc-400 md:text-sm"
                />
              </div>
            </div>
          </div>
        )}

        <div className="min-w-0 space-y-2">
          <Label
            htmlFor="email"
            className="text-xs font-semibold text-zinc-700 [-webkit-font-smoothing:antialiased]"
          >
            Work email
          </Label>
          <div className="relative min-w-0">
            <Mail
              aria-hidden
              className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
            />
            <Input
              ref={emailInputRef}
              id="email"
              name="email"
              type="email"
              dir="ltr"
              placeholder="you@blirumah.com"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              required
              maxLength={EMAIL_MAX_LEN}
              spellCheck={false}
              autoComplete="email"
              aria-invalid={error !== null}
              aria-describedby={error !== null ? SIGN_IN_ERROR_ID : undefined}
              className="h-11 min-w-0 border-zinc-200/90 bg-white/80 ps-10 text-base shadow-sm transition-[box-shadow,border-color] focus-visible:border-zinc-400 md:text-sm"
            />
          </div>
        </div>

        <div className="min-w-0 space-y-2">
          <Label
            htmlFor="password"
            className="text-xs font-semibold text-zinc-700 [-webkit-font-smoothing:antialiased]"
          >
            Password
          </Label>
          <div className="relative min-w-0">
            <Lock
              aria-hidden
              className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
            />
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Your password"
              required
              maxLength={PASSWORD_MAX_LEN}
              spellCheck={false}
              autoComplete={
                step === "signIn" ? "current-password" : "new-password"
              }
              autoCapitalize="none"
              autoCorrect="off"
              aria-invalid={error !== null}
              aria-describedby={error !== null ? SIGN_IN_ERROR_ID : undefined}
              className="h-11 min-w-0 border-zinc-200/90 bg-white/80 ps-10 text-base shadow-sm transition-[box-shadow,border-color] focus-visible:border-zinc-400 md:text-sm"
            />
          </div>
        </div>

        <input name="flow" type="hidden" value={step} />

        {error !== null && (
          <div
            id={SIGN_IN_ERROR_ID}
            role="alert"
            aria-live="assertive"
            className="flex min-w-0 flex-col gap-2 rounded-xl border border-red-200/80 bg-red-50/90 px-3 py-2.5 text-sm font-normal text-red-800 [-webkit-font-smoothing:antialiased]"
          >
            <div className="flex min-w-0 items-start gap-2.5">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
              <span className="min-w-0 flex-1 leading-snug [overflow-wrap:anywhere]">
                {error}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-9 w-full border-red-200/80 bg-white/80 text-xs font-semibold text-red-900 motion-safe:active:scale-[0.97] motion-reduce:active:scale-100 transition-all duration-150 ease-out"
              onClick={clearErrorAndFocusEmail}
            >
              Dismiss message and edit fields
            </Button>
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          className="h-11 w-full rounded-xl text-sm font-semibold shadow-md shadow-zinc-900/10 motion-safe:active:scale-[0.97] motion-reduce:active:scale-100 transition-all duration-150 ease-out cursor-pointer select-none bg-zinc-900 hover:bg-zinc-800"
          disabled={isLoading || authLoading}
        >
          {isLoading || authLoading
            ? step === "signIn"
              ? "Signing you in…"
              : "Creating your account…"
            : step === "signIn"
              ? "Sign in"
              : "Sign up"}
        </Button>

        <div className="flex min-w-0 items-start justify-center gap-2 rounded-xl border border-zinc-200/60 bg-zinc-50/50 px-3 py-2.5 text-start">
          <ShieldCheck
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600"
          />
          <p className="min-w-0 flex-1 text-xs font-normal leading-relaxed text-zinc-600 [-webkit-font-smoothing:antialiased] [text-rendering:optimizeLegibility] [overflow-wrap:anywhere]">
            Staff-only access. Never share your password, and reach out to your
            admin if you are locked out.
          </p>
        </div>

        <div className="text-center pt-1">
          {/* <button
            type="button"
            onClick={() => {
              setStep(step === "signIn" ? "signUp" : "signIn");
              setError(null);
            }}
            className="text-xs font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            {step === "signIn"
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </button> */}
        </div>
      </form>
    </div>
  );
}
